'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AlertCircle, FlaskConical, Loader2, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import { useDrugInteractionsPrefetch } from '../hooks/useDrugInteractionsPrefetch';
import { useFingerprint } from '../hooks/useFingerprint';
import {
  type CheckOptions,
  useInteractionCheck,
} from '../hooks/useInteractionCheck';
import { useUrlDrugSync } from '../hooks/useUrlDrugSync';
import { isExactExampleSet, isExampleSubset } from '../lib/example-drugs';
import type {
  ChplQuote,
  InteractionCheckResult,
  ViewChplOptions,
} from '../types';
import type { InteractionCheckPolicy } from './drug-interactions-page-shell';
import { type Drug, DrugSearch } from './drug-search';
import { EnglishUnavailableCard } from './english-unavailable-card';
import { InteractionPaywall, type PaywallVariant } from './interaction-paywall';
import { InteractionResults } from './interaction-results';
import { ResultsSkeleton } from './results-skeleton';
import {
  type CombinationDrug,
  TopCheckedCombinations,
} from './top-checked-combinations';

/** The demo holds cached results back for this long so the analysis "runs" on stage. */
const DEMO_MIN_DISPLAY_MS = 3000;

function TrialActivationButton({
  interactionCount,
  drugsCount,
}: {
  interactionCount?: number;
  drugsCount: number;
}) {
  const { t } = useTranslation('interactions');
  const hasInteractions = interactionCount != null && interactionCount > 0;

  return (
    <div className="flex flex-col items-center pt-2">
      <button
        type="button"
        onClick={() => {
          window.dispatchEvent(new CustomEvent('openTrialWizard'));
        }}
        className={cn(
          'flex flex-col items-start gap-0.5 rounded-lg border-2 px-6 py-3 text-white sm:flex-row sm:items-center sm:gap-2',
          'transition-all hover:translate-x-[2px] hover:translate-y-[2px]',
          'focus:ring-2 focus:ring-offset-2 focus:outline-none',
          'border-green-600 bg-green-700 hover:bg-green-800',
          'shadow-[3px_3px_0_0_#16a34a] hover:shadow-[1px_1px_0_0_#16a34a]',
          'focus:ring-green-500',
          'dark:border-green-400 dark:bg-green-700 dark:hover:bg-green-800',
          'dark:shadow-[3px_3px_0_0_#22c55e] dark:hover:shadow-[1px_1px_0_0_#22c55e]',
        )}
      >
        <span className="text-lg font-bold sm:text-base sm:font-semibold">
          {t('check.activateTrial')}
        </span>
        <span className="flex items-center gap-1.5 text-sm text-white/70 sm:text-base sm:font-light sm:text-white">
          <span className="sm:hidden">
            {hasInteractions
              ? t('check.toSeeInteractions', { count: interactionCount })
              : t('check.toCheckInteractions')}
          </span>
          <span className="hidden sm:inline">
            {hasInteractions
              ? t('check.andSeeInteractions', { count: interactionCount })
              : t('check.andCheckInteractions')}
          </span>
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1 text-xs font-bold text-white">
            {t('drugsCount', { count: drugsCount })}
          </span>
        </span>
      </button>
    </div>
  );
}

export interface DrugInteractionsContainerProps {
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Whether the user is authenticated */
  isAuthenticated?: boolean;
  /** Whether the auth state is still loading (prevents premature paywall variant) */
  isAuthLoading?: boolean;
  /** Whether the user has an active trial (or is verified) */
  hasActiveTrial?: boolean;
  /** Whether the authenticated user needs to activate their free trial first */
  needsTrialActivation?: boolean;
  /** Callback to trigger the trial activation wizard */
  onActivateTrial?: () => void;
  /** Initial drugs to preload in the search (e.g., from URL params) */
  initialDrugs?: Drug[];
  /** Server-rendered interaction results for canonical landing pages */
  initialInteractionResults?: InteractionCheckResult[];
  /** Access mode for server-rendered interaction results */
  initialInteractionAccess?: 'none' | 'full' | 'paywalled';
  /** Paywall variant for server-rendered interaction results */
  initialInteractionPaywallVariant?: PaywallVariant | null;
  /** Whether pricing may be shown (price + /pricing CTA in the expired paywall) */
  showPricing: boolean;
  /** Callback when the number of selected drugs changes */
  onDrugsCountChange?: (count: number) => void;
  /** Callback when the selected drugs list changes (used for looking up drug metadata) */
  onSelectedDrugsChange?: (drugs: Drug[]) => void;
  /** Callback that receives the reset function */
  onResetRef?: (reset: () => void) => void;
  /** Callback that receives the setDrugs function to programmatically set drugs */
  onSetDrugsRef?: (setDrugs: (drugs: Drug[]) => void) => void;
  /** When true, automatically trigger an interaction check after drugs are set via onSetDrugsRef */
  autoCheckOnSet?: boolean;
  /** Callback that receives the drug search scroll anchor ref */
  onDrugSearchRef?: (ref: React.RefObject<HTMLDivElement | null>) => void;
  /** Optional element rendered directly under the search input */
  searchFooter?: ReactNode;
  /** Content rendered inside the search input on the right (desktop only) */
  searchInputEndAddon?: ReactNode;
  /** Callback when user wants to view a ChPL document */
  onViewChpl?: (quote: ChplQuote, options?: ViewChplOptions) => void;
  /** Callback when user wants to view a patient leaflet (ulotka) */
  onViewUlotka?: (quote: ChplQuote) => void;
  /** Enable URL synchronization for sharing (syncs drugs and check state to URL) */
  enableUrlSync?: boolean;
  /** When true, show drugs without ChPL extraction (admin only) */
  showUnavailableDrugs?: boolean;
  /** Server-rendered top combinations for SEO */
  initialTopCombinations?: CombinationDrug[];
  /** Called when top combinations data availability changes */
  onTopCombinationsHasData?: (hasData: boolean) => void;
  /** Additional class names for the search input element */
  searchInputClassName?: string;
  /** Maximum drugs that can be selected (tier-dependent) */
  maxDrugs?: number;
  /** Account is on a dedicated (custom) plan — the drug-limit upsell points to contact */
  hasCustomPlan?: boolean;
  /**
   * `'example-only'`: only subsets of the example drug set may be checked
   * (English demo). Other sets show an explanatory card and never call the
   * interaction API, consume free checks, or log analytics.
   */
  checkPolicy?: InteractionCheckPolicy;
  /** Loads the example set and runs the example analysis (used by the blocked card) */
  onLoadExample?: () => void;
}

export function DrugInteractionsContainer({
  placeholder,
  isAuthenticated = false,
  isAuthLoading = false,
  hasActiveTrial = false,
  needsTrialActivation = false,
  onActivateTrial: _onActivateTrial,
  initialDrugs,
  initialInteractionResults,
  initialInteractionAccess = 'none',
  initialInteractionPaywallVariant = null,
  showPricing,
  onDrugsCountChange,
  onSelectedDrugsChange,
  onResetRef,
  onSetDrugsRef,
  autoCheckOnSet = false,
  onDrugSearchRef,
  searchFooter,
  searchInputEndAddon,
  onViewChpl,
  onViewUlotka,
  enableUrlSync = false,
  showUnavailableDrugs = false,
  initialTopCombinations,
  onTopCombinationsHasData,
  searchInputClassName,
  maxDrugs,
  hasCustomPlan = false,
  checkPolicy = 'default',
  onLoadExample,
}: DrugInteractionsContainerProps) {
  const exampleOnly = checkPolicy === 'example-only';
  const [serverInteractionState, setServerInteractionState] = useState<{
    results: InteractionCheckResult[];
    access: 'full' | 'paywalled';
    paywallVariant: PaywallVariant | null;
  } | null>(() =>
    initialInteractionResults && initialInteractionAccess !== 'none'
      ? {
          results: initialInteractionResults,
          access:
            initialInteractionAccess === 'paywalled' ? 'paywalled' : 'full',
          paywallVariant: initialInteractionPaywallVariant,
        }
      : null,
  );
  const [selectedDrugs, setSelectedDrugs] = useState<Drug[]>(
    initialDrugs ?? [],
  );
  const [showResults, setShowResults] = useState(
    () => serverInteractionState !== null,
  );
  const [userHasInteracted, setUserHasInteracted] = useState(!enableUrlSync);
  const [cacheProbeSeq, setCacheProbeSeq] = useState(0);
  const lastProbedSeq = useRef(0);
  const cacheProbeTokenRef = useRef(0);
  const [paywallVariant, setPaywallVariant] = useState<PaywallVariant | null>(
    null,
  );
  const [isPending, setIsPending] = useState(false);
  const [pendingAutoCheck, setPendingAutoCheck] = useState(false);
  // Set when the user asked for a check the demo policy does not allow
  const [showDemoBlocked, setShowDemoBlocked] = useState(false);
  const suppressResultsScrollRef = useRef(false);

  // True while we're probing the cache on URL-sync landing.
  // Prevents the "Sprawdź interakcje" button from flashing before
  // we know whether results are already cached.
  const [isProbing, setIsProbing] = useState(() => {
    const initialChplIds = (initialDrugs ?? [])
      .filter((d) => d.hasChplExtraction === true)
      .map((d) => d.id);

    return (
      serverInteractionState === null &&
      enableUrlSync &&
      initialChplIds.length >= 2 &&
      !needsTrialActivation &&
      // Blocked demo sets are never probed — the card shows right away
      !(exampleOnly && !isExampleSubset(initialChplIds))
    );
  });

  // Track ChPL drug IDs so handleDrugsChange can detect if analyzable drugs changed
  // without needing selectedDrugs in its dependency array.
  const prevChplIdsRef = useRef<Set<string>>(
    new Set(
      (initialDrugs ?? [])
        .filter((d) => d.hasChplExtraction === true)
        .map((d) => d.id),
    ),
  );
  const initialServerDrugsKey = useMemo(
    () =>
      [...(initialDrugs ?? [])]
        .filter((d) => d.hasChplExtraction === true)
        .map((d) => d.id)
        .sort()
        .join(':'),
    [initialDrugs],
  );

  const { t } = useTranslation('interactions');
  const { getFingerprint } = useFingerprint();

  const {
    checkInteractions,
    probeCache,
    isLoading: isChecking,
    results: interactionResults,
    errorKey: interactionErrorKey,
    progress: interactionProgress,
    reset: resetInteractionCheck,
    cancel: cancelInteractionCheck,
  } = useInteractionCheck();

  // URL sync callbacks - defined before hook usage
  const handleDrugsFromUrl = useCallback(
    (drugs: Drug[]) => {
      const urlDrugsKey = [...drugs]
        .filter((d) => d.hasChplExtraction === true)
        .map((d) => d.id)
        .sort()
        .join(':');

      prevChplIdsRef.current = new Set(
        drugs.filter((d) => d.hasChplExtraction === true).map((d) => d.id),
      );
      setSelectedDrugs(drugs);
      setShowDemoBlocked(false);
      if (urlDrugsKey !== initialServerDrugsKey) {
        setServerInteractionState(null);
      }
      resetInteractionCheck();
    },
    [resetInteractionCheck, initialServerDrugsKey],
  );

  const handleAutoCheck = useCallback(() => {
    setCacheProbeSeq((s) => s + 1);
  }, []);

  // URL synchronization hook
  const { syncDrugsToUrl, getShareableUrl } = useUrlDrugSync(
    enableUrlSync
      ? {
          onDrugsFromUrl: handleDrugsFromUrl,
          onAutoCheck: handleAutoCheck,
          initialDrugs,
        }
      : {},
  );

  const drugIds = useMemo(
    () => selectedDrugs.map((d) => d.id),
    [selectedDrugs],
  );

  // The demo never warms the LLM cache: its results are static files.
  useDrugInteractionsPrefetch(drugIds, userHasInteracted && !exampleOnly);

  // Ref to the drug search section for scrolling on mobile
  const drugSearchRef = useRef<HTMLDivElement>(null);

  // Ref to the results/waitlist section for scrolling
  const resultsSectionRef = useRef<HTMLDivElement>(null);
  const hasHandledInitialResultsScroll = useRef(false);

  // Track cancellation so we don't show partial results after cancel
  const isCancelledRef = useRef(false);

  const reset = useCallback(() => {
    // Mark any in-flight check as cancelled so it can't re-show results
    isCancelledRef.current = true;
    cacheProbeTokenRef.current += 1;
    prevChplIdsRef.current = new Set();
    setSelectedDrugs([]);
    setShowResults(false);
    setShowDemoBlocked(false);
    setPaywallVariant(null);
    setServerInteractionState(null);
    resetInteractionCheck();
    if (enableUrlSync) {
      syncDrugsToUrl([]);
    }
  }, [resetInteractionCheck, enableUrlSync, syncDrugsToUrl]);

  // Handle manual drug changes from DrugSearch
  const handleDrugsChange = useCallback(
    (drugs: Drug[]) => {
      setUserHasInteracted(true);
      cacheProbeTokenRef.current += 1;
      setIsProbing(false);

      // Check if the analyzable drugs (with ChPL extraction) actually changed.
      // If only a non-analyzable drug was added/removed, keep the ongoing analysis.
      const newChplIds = new Set(
        drugs.filter((d) => d.hasChplExtraction === true).map((d) => d.id),
      );
      const oldChplIds = prevChplIdsRef.current;
      const chplDrugsChanged =
        newChplIds.size !== oldChplIds.size ||
        [...newChplIds].some((id) => !oldChplIds.has(id));

      prevChplIdsRef.current = newChplIds;
      setSelectedDrugs(drugs);
      setShowDemoBlocked(false);

      if (chplDrugsChanged) {
        // Mark as cancelled so the in-flight runCheck won't re-show results
        isCancelledRef.current = true;
        setShowResults(false);
        setPaywallVariant(null);
        setServerInteractionState(null);
        resetInteractionCheck();
      }

      if (enableUrlSync) {
        syncDrugsToUrl(drugs);
      }
    },
    [resetInteractionCheck, enableUrlSync, syncDrugsToUrl],
  );

  useEffect(() => {
    onResetRef?.(reset);
  }, [onResetRef, reset]);

  // Expose setDrugs function to parent for programmatic drug setting
  const setDrugs = useCallback(
    (drugs: Drug[]) => {
      setUserHasInteracted(true);
      cacheProbeTokenRef.current += 1;
      prevChplIdsRef.current = new Set(
        drugs.filter((d) => d.hasChplExtraction === true).map((d) => d.id),
      );
      setSelectedDrugs(drugs);
      setShowResults(false);
      setShowDemoBlocked(false);
      setPaywallVariant(null);
      setServerInteractionState(null);
      resetInteractionCheck();
      if (enableUrlSync) {
        syncDrugsToUrl(drugs);
      }
      if (autoCheckOnSet) {
        setPendingAutoCheck(true);
      }
    },
    [resetInteractionCheck, enableUrlSync, syncDrugsToUrl, autoCheckOnSet],
  );

  useEffect(() => {
    onSetDrugsRef?.(setDrugs);
  }, [onSetDrugsRef, setDrugs]);

  useEffect(() => {
    onDrugSearchRef?.(drugSearchRef);
  }, [onDrugSearchRef]);

  // When trial activates while we have server-rendered results behind paywall,
  // unlock them immediately so the user sees results without a page reload.
  useEffect(() => {
    if (
      hasActiveTrial &&
      serverInteractionState?.paywallVariant === 'trial-available'
    ) {
      setServerInteractionState((prev) =>
        prev ? { ...prev, access: 'full', paywallVariant: null } : prev,
      );
    }
  }, [hasActiveTrial, serverInteractionState?.paywallVariant]);

  // Clear probing state when trial activation is needed
  useEffect(() => {
    if (needsTrialActivation) {
      setIsProbing(false);
    }
  }, [needsTrialActivation]);

  useEffect(() => {
    onDrugsCountChange?.(selectedDrugs.length);
  }, [selectedDrugs.length, onDrugsCountChange]);

  useEffect(() => {
    onSelectedDrugsChange?.(selectedDrugs);
  }, [selectedDrugs, onSelectedDrugsChange]);

  // Scroll to results when they are ready to display
  useEffect(() => {
    if (!hasHandledInitialResultsScroll.current) {
      hasHandledInitialResultsScroll.current = true;
      if (serverInteractionState !== null) {
        return;
      }
    }

    if (showResults && resultsSectionRef.current) {
      if (suppressResultsScrollRef.current) {
        suppressResultsScrollRef.current = false;
      } else {
        setTimeout(() => {
          resultsSectionRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    }
  }, [showResults, serverInteractionState]);

  // Check if all selected drugs are in the free example list
  const isExactFreeExample = useMemo(
    () => isExactExampleSet(drugIds),
    [drugIds],
  );

  // Example drug set is always free — unlock server-rendered paywall
  useEffect(() => {
    if (isExactFreeExample && serverInteractionState?.paywallVariant != null) {
      setServerInteractionState((prev) =>
        prev ? { ...prev, access: 'full', paywallVariant: null } : prev,
      );
    }
  }, [isExactFreeExample, serverInteractionState?.paywallVariant]);

  // Filter drugs by their ChPL extraction status
  // Drugs with extraction available (for analysis)
  const drugsWithChpl = useMemo(
    () => selectedDrugs.filter((d) => d.hasChplExtraction === true),
    [selectedDrugs],
  );
  const currentDrugsWithChplKey = useMemo(
    () =>
      [...drugsWithChpl]
        .map((d) => d.id)
        .sort()
        .join(':'),
    [drugsWithChpl],
  );

  // Drugs without extraction (will be skipped from analysis)
  const skippedDrugs = useMemo(
    () => selectedDrugs.filter((d) => d.hasChplExtraction === false),
    [selectedDrugs],
  );

  // Demo policy: only subsets of the example set may be checked
  const checksBlocked =
    exampleOnly &&
    drugsWithChpl.length >= 2 &&
    !isExampleSubset(drugsWithChpl.map((d) => d.id));

  const rawForcePaywall = process.env.NEXT_PUBLIC_PAYWALL_FORCE_VARIANT;
  const forcePaywallVariant: PaywallVariant | null =
    rawForcePaywall === 'anonymous' || rawForcePaywall === 'expired'
      ? rawForcePaywall
      : null;

  const resolvePaywallVariant =
    useCallback(async (): Promise<PaywallVariant | null> => {
      // The demo only ever checks example subsets, which are always free —
      // no fingerprinting and no free-check consumption on stage.
      if (exampleOnly) return null;

      if (
        serverInteractionState?.access === 'full' &&
        currentDrugsWithChplKey === initialServerDrugsKey
      ) {
        return null;
      }

      if (forcePaywallVariant && !isExactFreeExample) {
        return forcePaywallVariant;
      }

      if (isExactFreeExample || hasActiveTrial) return null;

      if (isAuthenticated && needsTrialActivation) return 'trial-available';
      if (isAuthenticated) return 'expired';

      try {
        const { visitorId, confidenceScore } = await getFingerprint();
        const res = await fetch('/api/free-checks/consume', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitorId, confidenceScore }),
        });
        const { allowed } = (await res.json()) as {
          allowed: boolean;
          remaining: number;
        };
        return allowed ? null : 'anonymous';
      } catch {
        return 'anonymous';
      }
    }, [
      exampleOnly,
      forcePaywallVariant,
      isExactFreeExample,
      hasActiveTrial,
      needsTrialActivation,
      isAuthenticated,
      getFingerprint,
      serverInteractionState,
      currentDrugsWithChplKey,
      initialServerDrugsKey,
    ]);

  // URL flow: probe cache for results without triggering processing.
  // If all pairs are cached, show results immediately (with paywall if needed).
  // If not, do nothing — the user sees the "Sprawdź interakcje" button.
  useEffect(() => {
    if (
      serverInteractionState !== null ||
      userHasInteracted ||
      cacheProbeSeq === lastProbedSeq.current ||
      drugsWithChpl.length < 2 ||
      isAuthLoading ||
      needsTrialActivation
    )
      return;

    lastProbedSeq.current = cacheProbeSeq;

    // Blocked demo sets never hit the API; the card is shown instead.
    if (checksBlocked) {
      setIsProbing(false);
      return;
    }

    const probeToken = cacheProbeTokenRef.current;

    void (async () => {
      try {
        const { allDone } = await probeCache(drugsWithChpl);
        if (probeToken !== cacheProbeTokenRef.current) {
          return;
        }

        if (!allDone) {
          setIsProbing(false);
          return;
        }

        const variant = await resolvePaywallVariant();
        if (probeToken !== cacheProbeTokenRef.current) {
          return;
        }

        setPaywallVariant(variant);
        setShowResults(true);
        setIsProbing(false);
      } catch {
        // Probe failed — user can still click the button
        setIsProbing(false);
      }
    })();
  }, [
    cacheProbeSeq,
    checksBlocked,
    drugsWithChpl,
    probeCache,
    resolvePaywallVariant,
    isAuthLoading,
    needsTrialActivation,
    serverInteractionState,
    userHasInteracted,
  ]);

  const runCheck = useCallback(
    async (options?: CheckOptions) => {
      setShowResults(false);
      isCancelledRef.current = false;

      if (drugsWithChpl.length < 2) {
        setShowResults(true);
        return;
      }

      try {
        await checkInteractions(drugsWithChpl, {
          ...options,
          // Demo results are static, so they would otherwise appear instantly.
          ...(exampleOnly ? { minDisplayMs: DEMO_MIN_DISPLAY_MS } : {}),
        });
        if (!isCancelledRef.current) setShowResults(true);
      } catch {
        if (!isCancelledRef.current) setShowResults(true);
      }
    },
    [drugsWithChpl, checkInteractions, exampleOnly],
  );

  const handleCheckInteractions = useCallback(
    async (options?: CheckOptions) => {
      setUserHasInteracted(true);

      // Demo policy: show the card, make no API call, consume nothing.
      if (checksBlocked) {
        setShowDemoBlocked(true);
        return;
      }

      setIsPending(true);

      try {
        const variant = await resolvePaywallVariant();
        setPaywallVariant(variant);

        return await runCheck(options);
      } finally {
        setIsPending(false);
      }
    },
    [checksBlocked, resolvePaywallVariant, runCheck],
  );

  // Auto-trigger check after drugs are set programmatically (e.g. example load)
  useEffect(() => {
    if (pendingAutoCheck && drugsWithChpl.length >= 2) {
      setPendingAutoCheck(false);
      suppressResultsScrollRef.current = true;
      void handleCheckInteractions({ skipDelay: true });
    }
  }, [pendingAutoCheck, drugsWithChpl, handleCheckInteractions]);

  // Generate shareable URL for results
  const shareableUrl = enableUrlSync
    ? getShareableUrl(selectedDrugs)
    : undefined;
  const displayedResults =
    interactionResults ?? serverInteractionState?.results ?? null;
  const displayedPaywallVariant =
    interactionResults != null
      ? paywallVariant
      : (serverInteractionState?.paywallVariant ?? paywallVariant);
  const hasInteractions =
    displayedResults?.some((result) => result.hasInteraction) ?? false;

  // Can check if at least 2 drugs with ChPL extraction are selected
  const canCheck = drugsWithChpl.length >= 2;
  const hasOneDrug = drugsWithChpl.length === 1;

  // The "not available in English" card: after the user asked for a blocked
  // check, or straight away when a blocked set was loaded from the URL.
  const demoBlockedVisible =
    checksBlocked && (showDemoBlocked || !userHasInteracted);
  const [hintShownAtDrugCount, setHintShownAtDrugCount] = useState<
    number | null
  >(null);
  const showAddDrugHint = hintShownAtDrugCount === drugsWithChpl.length;

  // Show top combinations when exactly 1 drug is selected and no results/check in progress
  const showTopCombinations =
    selectedDrugs.length === 1 &&
    !showResults &&
    !isChecking &&
    !isPending &&
    !isProbing;

  const singleDrug = showTopCombinations ? selectedDrugs[0] : null;

  return (
    <div
      className={cn('space-y-5', showTopCombinations && 'flex flex-1 flex-col')}
    >
      <div ref={drugSearchRef} className="scroll-mt-4" />
      <DrugSearch
        selectedDrugs={selectedDrugs}
        onDrugsChange={handleDrugsChange}
        placeholder={placeholder}
        footer={searchFooter}
        onViewChpl={onViewChpl}
        onViewUlotka={onViewUlotka}
        showUnavailableDrugs={showUnavailableDrugs}
        inputClassName={searchInputClassName}
        inputEndAddon={searchInputEndAddon}
        maxDrugs={maxDrugs}
        showPricing={showPricing}
        hasCustomPlan={hasCustomPlan}
      />

      {/* Skeleton shown during cache probe on URL-sync landings */}
      {canCheck && !showResults && isProbing && !needsTrialActivation && (
        <ResultsSkeleton />
      )}

      {/* Trial activation CTA — neubrutalism green button in place of "Sprawdź interakcje" */}
      {canCheck &&
        !isProbing &&
        needsTrialActivation &&
        (!showResults || displayedPaywallVariant === 'trial-available') && (
          <TrialActivationButton
            drugsCount={drugsWithChpl.length}
            interactionCount={
              displayedResults?.filter((r) => r.hasInteraction).length
            }
          />
        )}

      {/* Check interactions button — shown for users with active trial or anonymous users */}
      {(canCheck || hasOneDrug) &&
        !showResults &&
        !isProbing &&
        !demoBlockedVisible &&
        !needsTrialActivation && (
          <div className="flex flex-col items-center gap-4 pt-2">
            <div className="relative flex items-center justify-center">
              <Button
                size="xl"
                onClick={() => {
                  if (hasOneDrug) {
                    setHintShownAtDrugCount(drugsWithChpl.length);
                    return;
                  }
                  handleCheckInteractions();
                }}
                disabled={isPending || isChecking}
                aria-disabled={hasOneDrug}
                className={cn('gap-2', hasOneDrug && 'opacity-50')}
              >
                {isPending || isChecking ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>
                      {isChecking
                        ? t('check.analyzing', {
                            drugs: t('drugsCount', {
                              count: drugsWithChpl.length,
                            }),
                            pairs: t('pairsCount', {
                              count: interactionProgress?.total ?? 0,
                            }),
                          })
                        : t('check.preparing')}
                    </span>
                  </>
                ) : (
                  <>
                    <FlaskConical className="h-5 w-5" />
                    <span>{t('check.button')}</span>
                    <span className="ml-1 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white/20 px-1.5 text-sm font-bold">
                      {t('drugsCount', { count: drugsWithChpl.length })}
                    </span>
                  </>
                )}
              </Button>

              {/* Cancel button */}
              {(isPending || isChecking) && (
                <button
                  type="button"
                  onClick={() => {
                    isCancelledRef.current = true;
                    cancelInteractionCheck();
                  }}
                  title={t('check.cancel')}
                  className="absolute left-full ml-2 p-2 text-zinc-400 transition-colors hover:text-zinc-600 focus:outline-none dark:text-zinc-500 dark:hover:text-zinc-300"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {/* Hint to add another drug */}
            {showAddDrugHint && (
              <p
                role="status"
                className="animate-in fade-in text-sm text-red-400 dark:text-red-400/80"
              >
                {t('check.addOneMore')}
              </p>
            )}

            {/* Progress bar during analysis */}
            {(isPending || isChecking) &&
              interactionProgress &&
              interactionProgress.percentage > 0 && (
                <div className="w-full max-w-xs">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
                    <div
                      className="h-full rounded-full bg-zinc-600 transition-all duration-300 ease-out dark:bg-zinc-400"
                      style={{ width: `${interactionProgress.percentage}%` }}
                    />
                  </div>
                </div>
              )}
          </div>
        )}

      {/* Results / paywall section */}
      <div ref={resultsSectionRef} className="scroll-mt-24">
        {demoBlockedVisible && (
          <div className="mt-6 border-t-4 border-dashed border-zinc-300 pt-5 pb-10 @[640px]:pt-10 @[640px]:pb-16 dark:border-zinc-600">
            <EnglishUnavailableCard
              drugIds={selectedDrugs.map((d) => d.id)}
              onLoadExample={onLoadExample}
            />
          </div>
        )}
        {showResults &&
          displayedPaywallVariant !== 'trial-available' &&
          (interactionErrorKey ||
            isChecking ||
            (displayedResults && displayedResults.length > 0) ||
            skippedDrugs.length > 0) && (
            <div className="mt-6 border-t-4 border-dashed border-zinc-300 pt-5 pb-10 @[640px]:pt-10 @[640px]:pb-16 dark:border-zinc-600">
              {interactionErrorKey ? (
                <div className="flex items-center justify-between gap-4 rounded-lg border-2 border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                  <div className="flex items-center gap-3 text-red-700 dark:text-red-400">
                    <AlertCircle className="h-5 w-5 shrink-0" />
                    <span>{t(`common:${interactionErrorKey}`)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCheckInteractions()}
                    disabled={isChecking}
                    className={cn(
                      'flex shrink-0 items-center gap-2 rounded-md border border-red-400 bg-white px-3 py-1.5 text-sm font-medium text-red-700',
                      'transition-colors hover:bg-red-50',
                      'focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      'dark:border-red-600 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50',
                    )}
                  >
                    {isChecking ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    <span>{t('common:tryAgain')}</span>
                  </button>
                </div>
              ) : displayedPaywallVariant && hasInteractions ? (
                <>
                  <InteractionResults
                    drugs={drugsWithChpl}
                    results={displayedResults}
                    renderMode="title-only"
                    shareableUrl={shareableUrl}
                  />
                  <InteractionPaywall
                    variant={displayedPaywallVariant}
                    showPricing={showPricing}
                    interactionCount={
                      displayedResults?.filter(
                        (result) => result.hasInteraction,
                      ).length
                    }
                    severeCount={
                      displayedResults?.filter(
                        (result) =>
                          result.hasInteraction &&
                          (result.severity === 'critical' ||
                            result.severity === 'important'),
                      ).length
                    }
                  >
                    <InteractionResults
                      drugs={drugsWithChpl}
                      results={displayedResults}
                      renderMode="cards-only"
                    />
                  </InteractionPaywall>
                </>
              ) : hasInteractions ? (
                <InteractionPaywall contentVisible showPricing={showPricing}>
                  <InteractionResults
                    drugs={drugsWithChpl}
                    skippedDrugs={skippedDrugs}
                    results={displayedResults}
                    isLoading={isChecking}
                    progress={interactionProgress}
                    onViewChpl={onViewChpl}
                    shareableUrl={shareableUrl}
                  />
                </InteractionPaywall>
              ) : (
                <InteractionResults
                  drugs={drugsWithChpl}
                  skippedDrugs={skippedDrugs}
                  results={displayedResults}
                  isLoading={isChecking}
                  progress={interactionProgress}
                  onViewChpl={onViewChpl}
                  shareableUrl={shareableUrl}
                />
              )}
            </div>
          )}
      </div>

      {/* Top checked combinations — pushed to bottom via flex spacer */}
      {showTopCombinations && singleDrug && (
        <>
          <div className="min-h-16 flex-1" />
          <TopCheckedCombinations
            key={singleDrug.id}
            drugId={singleDrug.id}
            drugName={singleDrug.productName}
            drugStrength={singleDrug.strength}
            drugCommonName={singleDrug.commonName}
            drugForm={singleDrug.form}
            initialDrugs={
              initialDrugs?.[0]?.id === singleDrug.id
                ? initialTopCombinations
                : undefined
            }
            onHasData={onTopCombinationsHasData}
            onSelectDrug={(d) => {
              setDrugs([
                singleDrug,
                {
                  id: d.id,
                  productName: d.productName,
                  commonName: d.commonName,
                  strength: d.strength,
                  form: d.form,
                  hasChplPdf: d.hasChplPdf,
                  hasUlotkaPdf: d.hasUlotkaPdf,
                  hasChplExtraction: d.hasChplExtraction,
                },
              ]);
              // Scroll to top of drug search on mobile
              setTimeout(() => {
                drugSearchRef.current?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'start',
                });
              }, 100);
            }}
          />
        </>
      )}
    </div>
  );
}
